import React from 'react';
import {
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Checkbox,
	ListItemText,
	OutlinedInput,
	Stack,
	Paper,
	Box,
} from '@mui/material';
import { Text, rem } from '@innovyze/stylovyze';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { useGlobalization } from '@Translations';
import { WEAVE_FONT } from '@Utils/fonts';

interface DataSeries {
	id: string;
	name: string;
	data: number[][];
	color: string;
}

interface ChartSectionProps {
	availableDataSeries: DataSeries[];
	selectedLines: string[];
	setSelectedLines: (lines: string[]) => void;
	chartOptions: Highcharts.Options;
	// Rendered beneath the chart, for notes that describe the curve on screen. Kept as a slot so
	// this component stays presentational and knows nothing about survival curves.
	footer?: React.ReactNode;
}

export const ChartSection = ({
	availableDataSeries,
	selectedLines,
	setSelectedLines,
	chartOptions,
	footer,
}: ChartSectionProps) => {
	const { t } = useGlobalization();

	return (
		<Paper
			elevation={0}
			sx={{
				height: rem(600),
				display: 'flex',
				flexDirection: 'column',
				padding: rem(16),
				border: '1px solid #e5e7eb',
				borderRadius: rem(12),
			}}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: rem(16) }}>
				<Text style={{ fontFamily: WEAVE_FONT, fontWeight: 600, fontSize: '1.125rem' }}>
					{t('Survival Curve Preview')}
				</Text>
				{availableDataSeries.length > 0 && (
					<FormControl size="small" sx={{ minWidth: rem(320) }}>
						<InputLabel>{t('Compare curves (preview only)')}</InputLabel>
						<Select
							multiple
							value={selectedLines}
							onChange={event => {
								const value = event.target.value;
								setSelectedLines(typeof value === 'string' ? value.split(',') : value);
							}}
							input={<OutlinedInput label={t('Compare curves (preview only)')} />}
							renderValue={selected => (
								<Text>
									{availableDataSeries
										.filter(series => selected.includes(series.id))
										.map(series => series.name)
										.join(', ')}
								</Text>
							)}>
							{availableDataSeries.map(series => (
								<MenuItem key={series.id} value={series.id}>
									<Checkbox checked={selectedLines.indexOf(series.id) > -1} />
									<ListItemText primary={series.name} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
			</Stack>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<HighchartsReact
					highcharts={Highcharts}
					options={chartOptions}
					key={selectedLines.join('-')}
					containerProps={{ style: { height: '100%', width: '100%' } }}
				/>
			</Box>
			{/* Outside the flex:1 box so notes take their natural height and the chart absorbs the
			    difference, rather than the panel growing past its fixed height. */}
			{footer}
		</Paper>
	);
};
